import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Col, Form, Input, Row, Space, Typography } from 'antd';
import { SaveOutlined, ExperimentOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { get, patch } from '../../../api/client';
import type { GlobalPaymentConfig } from '../types';

/** 支付模式卡片选项（mock / real 两态，直观呈现当前收款状态） */
const MODE_OPTIONS = [
  {
    value: 'mock',
    title: '模拟支付',
    icon: <ExperimentOutlined />,
    color: '#f59e0b',
    desc: '本地 / 沙箱联调，走 mock-pay 直接完成，不产生真实扣款。上线前请务必切换为真实收款。',
  },
  {
    value: 'real',
    title: '真实收款',
    icon: <SafetyCertificateOutlined />,
    color: '#0d9488',
    desc: '按用户下单选择的渠道走真实支付。需先在「支付渠道」维护对应密钥并启用，否则下单会失败。',
  },
] as const;

/**
 * 全局支付配置：支付模式（mock/real）+ 异步通知基础地址 + 支付完成跳转地址。
 * 具体渠道密钥在「支付渠道」中维护，此处不涉及任何密钥。
 */
export function GlobalConfigPanel() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const [mode, setMode] = useState<GlobalPaymentConfig['paymentMode']>('mock');

  const { data, isLoading } = useQuery({
    queryKey: ['payment-config'],
    queryFn: () => get<GlobalPaymentConfig>('/admin/payment-config'),
  });

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ notifyUrl: data.notifyUrl, returnUrl: data.returnUrl });
      setMode(data.paymentMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const save = useMutation({
    mutationFn: (values: { notifyUrl: string; returnUrl: string }) =>
      patch('/admin/payment-config', { paymentMode: mode, notifyUrl: values.notifyUrl, returnUrl: values.returnUrl }),
    onSuccess: () => {
      message.success('全局支付配置已保存');
      void qc.invalidateQueries({ queryKey: ['payment-config'] });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '保存失败'),
  });

  return (
    <Card loading={isLoading} styles={{ body: { padding: 24 } }}>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        支付模式与回调地址为全局配置；各渠道的密钥与账户信息请在「支付渠道」标签页维护。
      </Typography.Paragraph>

      <Typography.Text strong>支付模式</Typography.Text>
      <Row gutter={[16, 16]} style={{ margin: '12px 0 24px' }}>
        {MODE_OPTIONS.map((opt) => {
          const active = mode === opt.value;
          return (
            <Col key={opt.value} xs={24} md={12}>
              <div
                onClick={() => setMode(opt.value)}
                style={{
                  cursor: 'pointer',
                  border: active ? `2px solid ${opt.color}` : '1px solid #eef0f1',
                  borderRadius: 14,
                  padding: 18,
                  background: active ? `${opt.color}0d` : '#fff',
                  transition: 'all .18s',
                  height: '100%',
                }}
              >
                <Space align="center" size={12}>
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 12,
                      background: `${opt.color}1a`,
                      color: opt.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                    }}
                  >
                    {opt.icon}
                  </div>
                  <div>
                    <Space size={8}>
                      <Typography.Text strong style={{ fontSize: 15 }}>
                        {opt.title}
                      </Typography.Text>
                      {active && (
                        <span style={{ fontSize: 12, color: opt.color, fontWeight: 600 }}>● 当前</span>
                      )}
                    </Space>
                    <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: '4px 0 0', lineHeight: 1.5 }}>
                      {opt.desc}
                    </Typography.Paragraph>
                  </div>
                </Space>
              </div>
            </Col>
          );
        })}
      </Row>

      <Form form={form} layout="vertical" style={{ maxWidth: 640 }} onFinish={(v) => save.mutate(v)}>
        <Form.Item
          name="notifyUrl"
          label="异步通知基础地址 (notifyUrl)"
          tooltip="支付服务商服务器回调的公网基础地址，必须外网可访问的 https。发起支付时系统自动追加渠道后缀（如 /alipay、/wechat），此处填到 .../payment/notify 即可。"
        >
          <Input placeholder="https://your-domain/api/payment/notify" />
        </Form.Item>
        <Form.Item name="returnUrl" label="支付完成跳转地址 (returnUrl)" tooltip="用户在渠道支付完成后同步跳回的页面地址。">
          <Input placeholder="https://your-domain/settings?tab=membership" />
        </Form.Item>
        <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={save.isPending}>
          保存全局配置
        </Button>
      </Form>
    </Card>
  );
}
